/**     ---- MY CURRENT LIVE PROFILEAPI-------.
 * services/profileApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * User profile, verification, and password methods.
 *
 * METHODS:
 *   getMyProfile(token)                         — fetch full profile with docs
 *   updateMyProfile(formData, token)            — update profile + upload images
 *   requestPasswordReset(email)                 — send password reset email
 *   confirmPasswordReset(uid, token, newPwd)    — confirm reset with token
 *   submitForVerification(token)                — submit ID docs for review
 *   adminSetUserVerification(userId, status, token) — admin approve/reject ID
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async getMyProfile(token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/auth/profile/`, {
        headers: { 'Authorization': `Token ${t}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (error) {
      console.error('❌ getMyProfile error:', error);
      return {};
    }
  },

  async updateMyProfile(formData, token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/auth/profile/update/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${t}` },
        // Do NOT set Content-Type — browser sets it with boundary for FormData
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      // ✅ Return full parsed JSON so caller can read profile_image_url
      return await res.json();
    } catch (error) {
      console.error('❌ updateMyProfile error:', error);
      throw error;
    }
  },

  async requestPasswordReset(email) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/password-reset/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } catch (error) {
      console.error('❌ requestPasswordReset error:', error);
      throw error;
    }
  },

  async confirmPasswordReset(uid, token, newPassword) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/password-reset/confirm/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } catch (error) {
      console.error('❌ confirmPasswordReset error:', error);
      throw error;
    }
  },

  async submitForVerification(token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/auth/submit-verification/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } catch (error) {
      console.error('❌ submitForVerification error:', error);
      throw error;
    }
  },

  // ── Admin: set user verification status ──────────────────────────────────
  // POST /api/admin/users/<userId>/verify/
  // Body: { verification_status: 'verified'|'partial'|'pending'|'unverified'|'rejected' }
  // Called by UsersView when admin clicks a verdict button in the Manage modal.
  // After success, UsersView re-fetches all users so the table badge updates immediately.

  async adminSetUserVerification(userId, verificationStatus, token) {
    try {
      const t = token || this._getToken();
      if (!t) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/verify/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_status: verificationStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { success: false, error: data.error || data.detail || `HTTP ${res.status}` };
      return { success: true, ...data };
    } catch (error) {
      console.error('❌ adminSetUserVerification error:', error);
      return { success: false, error: error.message };
    }
  }

  // ─── Chatbot ──────────────────────────────────────────────────────────────
};

